/**
 * MobileAppPreview — renders CMS content inside a CSS phone frame using the
 * actual ContentManagementRenderer component from the native app.
 *
 * How it stays in sync with the app:
 *  - ContentManagementRenderer is loaded at runtime from the native app source
 *    (NativeAppPreview/* aliases). Any changes to block rendering in the app
 *    automatically flow here.
 *  - colors and ui spacing are imported from the native app theme (pure TS files,
 *    no React Native dependencies). Changes to app colours/spacing flow here too.
 *  - The Vite alias `react-native → react-native-web` makes RN primitives work
 *    in the browser. See scripts/create-vite-dev-config.ts → resolve.alias.
 *  - react-native-render-html is polyfilled at
 *    src/web-polyfills/react-native-render-html.tsx — update if CmsHtmlBlock
 *    gains new RenderHTML props that affect visual output.
 *  - @expo/vector-icons is stubbed at src/web-polyfills/expo-vector-icons.tsx —
 *    add entries to ICON_MAP if new icons appear in ContentManagement components.
 *
 * Sync checklist (run when app ContentManagement components change):
 *  1. New RN package used?  → add Vite alias in create-vite-dev-config.ts
 *  2. New icon used?        → add to ICON_MAP in expo-vector-icons.tsx
 *  3. New RenderHTML prop?  → update react-native-render-html.tsx polyfill
 *  4. colors/ui changed?    → automatic (direct import)
 *  5. New block type added? → ContentBlock handles it automatically via the
 *                             ContentManagementRenderer import
 */
import React, { useEffect, useState } from 'react';
import { RADIUS } from 'DesignTokens';
// Direct import of app theme values — mirrored in web-polyfills for CI when native app is absent.
import { colors } from '../../web-polyfills/native-app-theme/colors';
import { ui, pageContent } from '../../web-polyfills/native-app-theme/ui';
import type { ContentItem } from '../../models/ContentManagementInterface';
import type FundOpportunity from '../../models/FundOpportunity';
import MobileAppFundUpdateCards, { type MobileAppFundUpdate } from './MobileAppFundUpdateCards';
import { usePreviewResolvedImages } from './mobileAppPreviewImages';
import MobileAppPreviewLoading from './MobileAppPreviewLoading';

// Phone frame dimensions (iPhone 14 Pro proportions)
const PHONE_WIDTH = 375;
const PHONE_HEIGHT = 750;
const FRAME_PADDING = 12; // phone bezel thickness
const STATUS_BAR_HEIGHT = 44;
const HOME_INDICATOR_HEIGHT = 24;

const CONTENT_WIDTH = PHONE_WIDTH - pageContent.paddingHorizontal * 2;

interface MobileAppPreviewProps {
  content: ContentItem[];
  fundOpportunity: FundOpportunity | null;
  previewMode?: 'opportunity' | 'fund-update';
  updateDateLabel?: string;
  /** Fund-update mode: several updates (newest first) instead of the single `content`. */
  fundUpdates?: MobileAppFundUpdate[];
}

type PreviewModules = {
  ContentManagementRenderer: React.ComponentType<any>;
  ScreenIntro: React.ComponentType<any>;
  ScreenSectionTitle: React.ComponentType<any>;
};

/**
 * ContentItem (web) and FundUpdateContentItem (app) have the same shape:
 * { type: string; id: string; properties: Record<string, unknown> }
 * so we can cast safely.
 */
export default function MobileAppPreview({
  content,
  fundOpportunity,
  previewMode = 'opportunity',
  updateDateLabel,
  fundUpdates,
}: MobileAppPreviewProps) {
  const [modules, setModules] = useState<PreviewModules | null>(null);
  const resolvedContent = usePreviewResolvedImages(content);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      import('NativeAppPreview/ContentManagement'),
      import('NativeAppPreview/ScreenIntro'),
      import('NativeAppPreview/ScreenSectionTitle'),
    ]).then(([contentManagement, screenIntro, screenSectionTitle]) => {
      if (cancelled) {
        return;
      }

      setModules({
        ContentManagementRenderer: contentManagement.ContentManagementRenderer,
        ScreenIntro: screenIntro.ScreenIntro,
        ScreenSectionTitle: screenSectionTitle.ScreenSectionTitle,
      });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!modules) {
    return <MobileAppPreviewLoading />;
  }

  const { ContentManagementRenderer, ScreenIntro, ScreenSectionTitle } = modules;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '24px 0',
        background: '#e8eaed',
        borderRadius: RADIUS.sectionChild,
        minHeight: '100%',
      }}
    >
      {/* Phone outer shell */}
      <div
        style={{
          width: PHONE_WIDTH + FRAME_PADDING * 2,
          boxSizing: 'border-box',
          background: '#1c1c1e',
          borderRadius: 48,
          padding: FRAME_PADDING,
          boxShadow: '0 24px 64px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.08)',
          position: 'relative',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Dynamic island */}
        <div
          style={{
            width: 120,
            height: 34,
            background: '#000',
            borderRadius: 20,
            margin: '0 auto 0',
            marginBottom: -34,
            position: 'relative',
            zIndex: 2,
          }}
        />

        {/* Screen */}
        <div
          style={{
            width: PHONE_WIDTH,
            height: PHONE_HEIGHT,
            background: colors.background,
            borderRadius: 36,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Status bar */}
          <div
            style={{
              height: STATUS_BAR_HEIGHT,
              background: colors.surface,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 20px',
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, color: colors.text }}>9:41</span>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: colors.text }}>●●●●</span>
              <span style={{ fontSize: 10, color: colors.text }}>WiFi</span>
              <span style={{ fontSize: 10, color: colors.text }}>100%</span>
            </div>
          </div>

          {/* Nav bar (mimics app top bar) */}
          <div
            style={{
              height: 52,
              background: colors.surface,
              borderBottomWidth: 1,
              borderBottomColor: colors.borderLight,
              borderBottomStyle: 'solid',
              display: 'flex',
              alignItems: 'center',
              padding: '0 16px',
              gap: 12,
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 20, color: colors.primary }}>‹</span>
            <span
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: colors.text,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {fundOpportunity?.name ?? 'Opportunity'}
            </span>
          </div>

          {/* Scrollable content area */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: `${pageContent.paddingTop}px ${pageContent.paddingHorizontal}px`,
              display: 'flex',
              flexDirection: 'column',
              gap: ui.sectionGap,
              background: colors.background,
            }}
          >
            {previewMode === 'fund-update' ? (
              <MobileAppFundUpdateCards
                updates={fundUpdates ?? [{ dateLabel: updateDateLabel, content }]}
                contentWidth={CONTENT_WIDTH}
                ContentManagementRenderer={ContentManagementRenderer}
                ScreenSectionTitle={ScreenSectionTitle}
              />
            ) : (
              <>
                {/* Fund intro — same component as app's OpportunityDetailScreen */}
                {fundOpportunity && (
                  <ScreenIntro
                    title={fundOpportunity.name ?? ''}
                    subtitle={fundOpportunity.summaryTitle ?? undefined}
                    hideLastUpdated
                  />
                )}

                {/* Opportunity Information card + filter (static/disabled) */}
                <div
                  style={{
                    backgroundColor: colors.surface,
                    borderRadius: ui.cardRadius,
                    padding: '16px',
                  }}
                >
                  <ScreenSectionTitle>Opportunity information</ScreenSectionTitle>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ fontSize: 12, color: colors.textSecondary }}>
                      Currently viewing:
                    </span>
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '4px 10px',
                        borderRadius: 20,
                        border: `1px solid ${colors.borderLight}`,
                        backgroundColor: colors.surfaceVariant,
                        opacity: 0.7,
                      }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 600, color: colors.text }}>All</span>
                      <span style={{ fontSize: 10, color: colors.textSecondary }}>▾</span>
                    </div>
                  </div>
                </div>

                <ContentManagementRenderer
                  content={resolvedContent as any}
                  contentWidth={CONTENT_WIDTH}
                />

                {/* EOI stub at bottom */}
                <div
                  style={{
                    background: colors.surface,
                    borderRadius: ui.cardRadius,
                    border: `1px solid ${colors.borderLight}`,
                    padding: 16,
                    marginTop: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: colors.text,
                      marginBottom: 8,
                    }}
                  >
                    Expression of Interest
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: colors.textSecondary,
                      marginBottom: 16,
                      lineHeight: 1.5,
                    }}
                  >
                    Submit your indicative investment amount to express interest in this
                    opportunity.
                  </div>
                  <div
                    style={{
                      background: colors.surfaceVariant,
                      borderRadius: ui.panelRadius,
                      padding: '10px 12px',
                      marginBottom: 12,
                      fontSize: 13,
                      color: colors.textSecondary,
                      border: `1px solid ${colors.borderLight}`,
                    }}
                  >
                    Indicative amount ($)
                  </div>
                  <div
                    style={{
                      background: colors.primary,
                      borderRadius: ui.buttonRadius,
                      padding: '12px 16px',
                      textAlign: 'center',
                      color: '#fff',
                      fontSize: 14,
                      fontWeight: 600,
                      opacity: 0.7,
                    }}
                  >
                    Submit Expression of Interest
                  </div>
                </div>
              </>
            )}

            <div style={{ height: HOME_INDICATOR_HEIGHT }} />
          </div>

          {/* Home indicator */}
          <div
            style={{
              height: HOME_INDICATOR_HEIGHT,
              background: colors.background,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 120,
                height: 5,
                background: colors.text,
                borderRadius: 3,
                opacity: 0.2,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
